/**
 * Editor for a single DocumentTemplate.
 *
 * Fetches TWO shapes in parallel:
 *   - `raw`: the DocumentTemplate row itself (nullable fields preserved),
 *     used for edit-mode form pre-population.
 *   - `effective`: resolved payload with sections/questions cascaded from
 *     the root, used for display. A customized child template that has no
 *     direct section rows still shows its inherited sections here.
 *
 * Edit is always available. If the current template is inherited (not
 * owned at the viewing scope), saving silently customizes first — creating
 * a child at this scope — then applies the edits to the new child and
 * redirects the manager to that child.
 */
import { useEffect, useState } from 'react'
import { ArrowLeft, Edit2, Plus, Save } from 'lucide-react'
import Badge from '../ui/Badge'
import SectionEditor from './SectionEditor'
import SamplesPanel from './SamplesPanel'
import AddSectionModal from './AddSectionModal'
import {
  customizeTemplate,
  getEffectiveTemplate,
  getTemplate,
  updateTemplate,
} from './api'
import { isOwnedAtScope } from './cascade'
import { OWNER_LEVEL_BADGE } from './types'
import type {
  DocumentTemplate,
  ResolvedSection,
  ResolvedTemplate,
  Scope,
  Section,
  SectionSpecMembership,
} from './types'

interface Props {
  templateId: string
  scope: Scope
  scopeContext: { userId?: number; officeId?: number; programId?: number }
  onBack: () => void
  onCustomized?: (newTemplateId: string) => void
}

/** Build a Section-shaped object from a ResolvedSection so the existing
 * SectionEditor component can render it unchanged. Known limitation: we
 * synthesize `section_questions` from the resolved specs; the backend
 * still owns the source of truth for IDs/memberships. */
function resolvedToSection(rs: ResolvedSection, rootTemplateId: string): Section {
  return {
    id: rs.id,
    template: rs.template_id || rootTemplateId,
    key: rs.key,
    title: rs.title,
    order: rs.order,
    owner_level: rs.owner_level,
    owner_office: null,
    owner_user: null,
    owner_program: null,
    parent: null,
    is_removed: false,
    description: rs.description,
    writing_agent_instructions: rs.writing_agent_instructions,
    writing_skill: rs.writing_skill,
    skill_config: rs.skill_config,
    depends_on: rs.depends_on,
    depends_on_all: rs.depends_on_all,
    section_questions: rs.questions.map((q) => ({
      id: q.membership_id ?? 0,
      section: rs.id,
      spec: q.id,
      spec_detail: {
        id: q.id,
        key: q.key,
        name: q.name,
        description: null,
        spec_type: q.spec_type,
        category: q.category,
        unit: q.unit,
        category_options: q.category_options,
        owner_level: q.owner_level,
        owner_office: null,
        owner_user: null,
        owner_program: null,
        parent: null,
        is_removed: false,
        prompt: q.prompt,
        guidance: q.guidance,
        interview_priority: q.priority,
        eligible_respondent_roles: q.eligible_respondent_roles,
      },
      order: q.order,
      required: q.required,
    })) as SectionSpecMembership[],
  }
}

export default function DocumentTemplateEditor({
  templateId,
  scope,
  scopeContext,
  onBack,
  onCustomized,
}: Props) {
  const [raw, setRaw] = useState<DocumentTemplate | null>(null)
  const [effective, setEffective] = useState<ResolvedTemplate | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingMeta, setEditingMeta] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [instructions, setInstructions] = useState('')
  const [saving, setSaving] = useState(false)
  const [confirmPublish, setConfirmPublish] = useState(false)
  const [addSectionOpen, setAddSectionOpen] = useState(false)

  const refresh = async () => {
    try {
      const [rawData, effectiveData] = await Promise.all([
        getTemplate(templateId),
        getEffectiveTemplate(templateId),
      ])
      setRaw(rawData)
      setEffective(effectiveData)
      setName(rawData.name)
      setDescription(rawData.description || '')
      setInstructions(rawData.writing_agent_instructions || '')
    } catch (err: any) {
      setError(err.message || 'Failed to load template')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
  }, [templateId])

  if (loading) return <div className="p-6 text-muted-foreground">Loading template…</div>
  if (error) return <div className="p-6 text-destructive">{error}</div>
  if (!raw || !effective) return null

  const isOwned = isOwnedAtScope(raw, scope, scopeContext)
  const rootTemplateId = effective.root_id || raw.id
  const canAutoCustomize = !isOwned && scope.level !== 'system' && !!scope.ownerId

  const handleSaveMeta = async () => {
    setSaving(true)
    setError(null)
    try {
      let targetId = raw.id
      // Inherited template → auto-customize before applying the edit, so
      // the PATCH lands on a child owned at the current scope rather than
      // mutating the upstream template.
      if (canAutoCustomize) {
        const body: any = { owner_level: scope.level }
        if (scope.level === 'office') body.owner_office = scope.ownerId
        if (scope.level === 'program_manager') body.owner_user = scope.ownerId
        if (scope.level === 'program') body.owner_program = scope.ownerId
        const child = await customizeTemplate(raw.id, body)
        targetId = child.id
      }
      await updateTemplate(targetId, {
        name,
        description: description || null,
        writing_agent_instructions: instructions || null,
      })
      setEditingMeta(false)
      if (targetId !== raw.id) {
        // Redirect the manager to the new child — this triggers a re-fetch.
        onCustomized?.(targetId)
      } else {
        await refresh()
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save template')
    } finally {
      setSaving(false)
    }
  }

  // Question-based sections with no specs get written from instructions alone
  // (no program input), so a draft with any is flagged before publishing.
  const specLessSections = effective.sections.filter(
    (s) => (s.writing_skill ?? 'question_based_section') === 'question_based_section' && s.questions.length === 0,
  )

  const handlePublish = async () => {
    if (specLessSections.length > 0 && !confirmPublish) {
      setConfirmPublish(true)
      return
    }
    setConfirmPublish(false)
    setSaving(true)
    setError(null)
    try {
      await updateTemplate(raw.id, { is_active: true })
      await refresh()
    } catch (err: any) {
      setError(err.message || 'Failed to publish template')
    } finally {
      setSaving(false)
    }
  }

  const nextSectionOrder = effective.sections.reduce((m, s) => Math.max(m, s.order + 1), 0)

  const badge = OWNER_LEVEL_BADGE[raw.owner_level]

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" />
          All templates
        </button>
      </div>

      {!raw.is_active && (
        <div className="rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-foreground">
          This template is a draft. Review its sections, then publish it to make it
          available for program documents.
          {specLessSections.length > 0 && (
            <p className="mt-2">
              {specLessSections.length === 1 ? '1 section has' : `${specLessSections.length} sections have`} no
              specs, so {specLessSections.length === 1 ? 'it' : 'they'}'ll be written from instructions only,
              without program input: {specLessSections.map((s) => s.title).join(', ')}. Add specs to{' '}
              {specLessSections.length === 1 ? 'it' : 'them'} or publish anyway.
            </p>
          )}
        </div>
      )}

      <div className="bg-card border border-border rounded-lg p-6 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-semibold text-foreground">{effective.name}</h1>
              <Badge variant={badge.variant}>{badge.label}</Badge>
              {!isOwned && scope.level !== 'system' && (
                <Badge variant="pending">Inherited · read-only</Badge>
              )}
              {raw.is_removed && <Badge variant="rejected">Removed</Badge>}
              {!raw.is_active && <Badge variant="warning">Draft</Badge>}
            </div>
            <div className="text-xs font-mono text-muted-foreground mt-1">{effective.key}</div>
          </div>
          <div className="flex gap-2">
            {!raw.is_active && isOwned && (
              <button
                type="button"
                onClick={handlePublish}
                disabled={saving}
                className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md disabled:opacity-50"
              >
                {saving ? 'Publishing…' : confirmPublish ? 'Publish anyway' : 'Publish'}
              </button>
            )}
            {!editingMeta && (
              <button
                type="button"
                onClick={() => setEditingMeta(true)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-border rounded-md"
              >
                <Edit2 className="w-3.5 h-3.5" />
                Edit
              </button>
            )}
          </div>
        </div>

        {editingMeta ? (
          <div className="space-y-3">
            <label className="block">
              <span className="text-sm font-medium">Name</span>
              <input
                className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Description</span>
              <textarea
                className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Leave empty to inherit from upstream"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Writing agent instructions</span>
              <textarea
                className="mt-1 w-full px-3 py-2 rounded-md border border-input bg-background text-foreground font-mono text-xs"
                rows={4}
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Leave empty to inherit from upstream"
              />
            </label>
            {error && <div className="text-sm text-destructive">{error}</div>}
            <div className="flex items-center justify-between gap-2">
              {canAutoCustomize ? (
                <div className="text-xs text-muted-foreground italic">
                  Saving will create a customization at this scope.
                </div>
              ) : <div />}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditingMeta(false)}
                  className="px-3 py-1.5 text-sm border border-border rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveMeta}
                  disabled={saving}
                  className="inline-flex items-center gap-1 px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded-md disabled:opacity-50"
                >
                  <Save className="w-3.5 h-3.5" />
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {effective.description ? (
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-1">Description</div>
                <div className="text-sm text-foreground whitespace-pre-wrap">{effective.description}</div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground italic">No description.</div>
            )}
            {effective.writing_agent_instructions ? (
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-1">Writing agent instructions</div>
                <div className="text-sm text-foreground whitespace-pre-wrap font-mono">
                  {effective.writing_agent_instructions}
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground italic">No writing agent instructions.</div>
            )}
          </div>
        )}
      </div>

      {/* Sections — rendered from the resolved payload so inherited sections
          show up at customized children. */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">Sections</h2>
          <button
            type="button"
            onClick={() => setAddSectionOpen(true)}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md"
          >
            <Plus className="w-3.5 h-3.5" />
            Add section
          </button>
        </div>
        {effective.sections.length === 0 && (
          <div className="text-sm text-muted-foreground italic">No sections yet.</div>
        )}
        <div className="space-y-3">
          {effective.sections
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((rs) => {
              const sectionOwnedAtThisScope =
                isOwned &&
                rs.owner_level === scope.level &&
                // A section is "owned at this scope" only if its ownership level
                // matches the current scope. For V1 section-level customize is
                // deferred, so inherited sections (system at a non-system
                // template) are presented read-only.
                (rs.owner_level === 'system' ||
                  (rs.owner_level === 'office' && scopeContext.officeId != null) ||
                  (rs.owner_level === 'program_manager' && scopeContext.userId != null) ||
                  (rs.owner_level === 'program' && scopeContext.programId != null))
              const siblingKeys = effective.sections
                .filter((s) => s.key !== rs.key)
                .sort((a, b) => a.order - b.order)
                .map((s) => s.key)
              return (
                <SectionEditor
                  key={rs.id}
                  section={resolvedToSection(rs, rootTemplateId)}
                  scope={scope}
                  isOwned={sectionOwnedAtThisScope}
                  siblingKeys={siblingKeys}
                  onChanged={refresh}
                />
              )
            })}
        </div>
      </div>

      {/* Samples — from the raw template row (samples don't cascade). */}
      <div className="pt-4 border-t border-border">
        <SamplesPanel
          templateId={raw.id}
          samples={raw.samples}
          readOnly={!isOwned}
          onChanged={refresh}
        />
      </div>

      <AddSectionModal
        isOpen={addSectionOpen}
        onClose={() => setAddSectionOpen(false)}
        targetTemplateId={rootTemplateId}
        scope={scope}
        nextOrder={nextSectionOrder}
        excludeSectionIds={effective.sections.map((s) => s.id)}
        onDone={refresh}
      />
    </div>
  )
}
